import { useChannelState } from "../../lib/channelContext";
import { Col, Row } from "react-grid-system";
import Note from "./Note";
import {
  ResultDropdowns,
  ResultOption,
  ValidationErrors,
} from "../../lib/api/models";
import { PaymentData, ResultDataByTab } from "./resultTypes";
import StyledInput from "../common/inputs/StyledInput";
import {
  getCallBackDateMinMax,
  getPaymentDateMinMax,
  getPromiseDateMinMax,
  getResultActionEntry,
  getResultPaymentDateDefault,
  getWorkDateMinMax,
  paymentMethodRequiresCallBack,
} from "./resultUtils";
import styles from "./Results.module.css";
import PermCollQueue from "./PermCollQueue";
import Select from "../common/inputs/Select";
import DateInput from "../common/inputs/DateInput";
import classNames from "classnames";
import { centsToDollarString, dollarStringToCents } from "../../lib/formatting";
import { useState } from "react";
import ConfirmationModal from "../common/modals/ConfirmationModal";

const strings = {
  select: "Select",

  payment: "Payment",
  actionCd: "Action Cd",
  method: "Method",

  transferSource: "Transfer Source",
  callReason: "Call Reason",

  paymentDate: "Payment Dt",
  promiseDate: "Promise Dt",
  workDate: "Work Dt",
  callBackDate: "Call Back Dt",

  confirmNum: "Confirm #",

  actionEntry: "Action Entry",

  a0122Title: "A_0122",
  a0123Title: "A_0123",
  a0122Body:
    "The Payment Method selected may have default Payment, Promise, and/or Work Dates. Do you want to clear all dates and display the new default dates?",
  a0123Body: (isCallBack: boolean) =>
    `The ${isCallBack ? "Call Back Date" : "Payment Date"} selected may have ${isCallBack ? "a default Work Date" : "default Promise and/or Work Dates"}. Do you want to clear all dependent dates and display the new default dates?`,
  overrideCancelText: "No, Keep Values",
  overrideConfirmText: "Yes, Update Values",
};

type Props = {
  dwbuid: string;
  data: PaymentData;
  setData: React.Dispatch<React.SetStateAction<ResultDataByTab>>;
  options: ResultDropdowns;
  contactPerson: ResultOption | null;
  onDisplayGeneralErrors: (error: unknown) => void;
  validationMessages?: ValidationErrors;
  setValidationMessages: React.Dispatch<
    React.SetStateAction<ValidationErrors | undefined>
  >;
  referenceId?: string;
};

export default function PaymentContent({
  dwbuid,
  data,
  setData,
  options,
  contactPerson,
  onDisplayGeneralErrors,
  validationMessages,
  setValidationMessages,
  referenceId,
}: Props) {
  const { channel } = useChannelState();

  const [isDateOverrideOpenWith, setIsDateOverrideOpenWith] =
    useState<Partial<PaymentData> | null>(null);

  const setDataHandleDatesClearValidation = (
    newData: Partial<PaymentData>,
    overrideAndSetDate?: boolean | undefined,
  ) => {
    let fullNewData = newData;

    const isPaymentMethodOverride =
      fullNewData.paymentMethod &&
      (data.paymentDate || data.promiseDate || data.workDate);
    const isPaymentDateOverride =
      fullNewData.paymentDate && (data.promiseDate || data.workDate);
    const isCallBackDateOverride = fullNewData.callBackDate && data.workDate;

    const mustCheckOverride =
      overrideAndSetDate === undefined &&
      (isPaymentMethodOverride ||
        isPaymentDateOverride ||
        isCallBackDateOverride);

    // Date defaults need to be updated
    // Requires confirmation because dependencies have updated and current values exist
    if (mustCheckOverride) {
      setIsDateOverrideOpenWith(fullNewData);
      return;
    }

    // User chose to update date values
    // Or date default dependencies can be updated silently
    if (
      overrideAndSetDate === true ||
      (fullNewData.paymentMethod && !isPaymentMethodOverride) ||
      (fullNewData.paymentDate && !isPaymentDateOverride) ||
      (fullNewData.callBackDate && !isCallBackDateOverride)
    ) {
      const shouldClearDates = !!fullNewData.paymentMethod;
      const updatedData = {
        ...data,
        ...fullNewData,
      };
      fullNewData = {
        ...fullNewData,
        ...getResultPaymentDateDefault(
          {
            paymentMethod: updatedData.paymentMethod,
            paymentDate: updatedData.paymentDate,
            promiseDate: updatedData.promiseDate,
            workDate: updatedData.workDate,
            callBackDate: updatedData.callBackDate,
          },
          shouldClearDates,
        ),
      };
    }

    // Clear validation for editted fields
    setValidationMessages((v) => {
      let remainingValidation: ValidationErrors = v ?? {};
      Object.keys(fullNewData).forEach((key) => {
        const { [`PAYMENT.${key}`]: _, ...rest } = remainingValidation;
        remainingValidation = rest;
      });
      return remainingValidation;
    });

    setData((d) => ({
      ...d,
      PAYMENT: {
        ...d["PAYMENT"],
        ...fullNewData,
      },
    }));
  };

  return (
    <>
      <Row>
        <Col md={3}>
          <StyledInput
            variant="horizontal"
            className={styles.inputDivider}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.payment}
            value={data.paymentAmount}
            onChange={(e) =>
              setDataHandleDatesClearValidation({
                paymentAmount: e.target.value,
              })
            }
            onBlur={() => {
              try {
                const parsed = dollarStringToCents(
                  data.paymentAmount,
                  strings.payment,
                );
                if (!data.paymentAmount || !parsed || isNaN(parsed)) {
                  return;
                }
                const formatted = centsToDollarString(parsed);
                setDataHandleDatesClearValidation({
                  paymentAmount: formatted,
                });
              } catch {
                /* Payment amount cannot be validated, will fail on submission */
              }
            }}
            validationMessages={validationMessages?.paymentAmount}
            showPopover={false}
          />
          {options.actionCode && (
            <Select
              variant="horizontal"
              className={classNames(
                styles.selectContainer,
                styles.inputDivider,
              )}
              innerContentClassName={styles.inputContent}
              labelClassName={styles.inputLabel}
              inputClassName={styles.inputValue}
              label={strings.actionCd}
              placeholder={strings.select}
              selectedValue={data.actionCode?.value}
              options={options.actionCode.map((item) => ({
                key: item.id,
                label: item.description,
                value: item.value,
              }))}
              onChange={(value) => {
                if (!options.actionCode) {
                  // NOTE: This should not be possible
                  return;
                }
                const item = options.actionCode.find((i) => i.value === value);
                if (!item) {
                  // NOTE: This should not be possible
                  return;
                }
                setDataHandleDatesClearValidation({ actionCode: item });
              }}
              validationMessages={validationMessages?.actionCode}
              showPopover={false}
            />
          )}
          {options.paymentMethod && (
            <Select
              variant="horizontal"
              className={classNames(
                styles.selectContainer,
                styles.inputDivider,
              )}
              innerContentClassName={styles.inputContent}
              labelClassName={styles.inputLabel}
              inputClassName={styles.inputValue}
              label={strings.method}
              placeholder={strings.select}
              selectedValue={data.paymentMethod?.value}
              options={options.paymentMethod.map((item) => ({
                key: item.id,
                label: item.description,
                value: item.value,
              }))}
              onChange={(value) => {
                if (!options.paymentMethod) {
                  // NOTE: This should not be possible
                  return;
                }
                const item = options.paymentMethod.find(
                  (i) => i.value === value,
                );
                if (!item) {
                  // NOTE: This should not be possible
                  return;
                }
                setDataHandleDatesClearValidation({ paymentMethod: item });
              }}
              validationMessages={validationMessages?.paymentMethod}
              showPopover={false}
            />
          )}
          {channel === "OUT" || channel === "RES" || channel === "COR" ? (
            <StyledInput
              variant="horizontal"
              className={styles.inputDivider}
              innerContentClassName={styles.inputContent}
              labelClassName={styles.inputLabel}
              inputClassName={styles.inputValue}
              label={strings.confirmNum}
              value={data.confirmationNumber}
              onChange={(e) =>
                setDataHandleDatesClearValidation({
                  confirmationNumber: e.target.value,
                })
              }
              validationMessages={validationMessages?.confirmationNumber}
              showPopover={false}
            />
          ) : channel === "INB" && options.inboundCallReason ? (
            <Select
              variant="horizontal"
              className={classNames(
                styles.selectContainer,
                styles.inputDivider,
              )}
              innerContentClassName={styles.inputContent}
              labelClassName={styles.inputLabel}
              inputClassName={styles.inputValue}
              label={strings.callReason}
              placeholder={strings.select}
              selectedValue={data.inboundReason?.value}
              options={options.inboundCallReason.map((item) => ({
                key: item.id,
                label: item.description,
                value: item.value,
              }))}
              onChange={(value) => {
                if (!options.inboundCallReason) {
                  // NOTE: This should not be possible
                  return;
                }
                const item = options.inboundCallReason.find(
                  (i) => i.value === value,
                );
                if (!item) {
                  // NOTE: This should not be possible
                  return;
                }
                setDataHandleDatesClearValidation({ inboundReason: item });
              }}
              validationMessages={validationMessages?.inboundReason}
              showPopover={false}
            />
          ) : channel === "TRN" && options.transferSource ? (
            <Select
              variant="horizontal"
              className={classNames(
                styles.selectContainer,
                styles.inputDivider,
              )}
              innerContentClassName={styles.inputContent}
              labelClassName={styles.inputLabel}
              inputClassName={styles.inputValue}
              label={strings.transferSource}
              placeholder={strings.select}
              selectedValue={data.transferSource?.value}
              options={options.transferSource.map((item) => ({
                key: item.id,
                label: item.description,
                value: item.value,
              }))}
              onChange={(value) => {
                if (!options.transferSource) {
                  // NOTE: This should not be possible
                  return;
                }
                const item = options.transferSource.find(
                  (i) => i.value === value,
                );
                if (!item) {
                  // NOTE: This should not be possible
                  return;
                }
                setDataHandleDatesClearValidation({ transferSource: item });
              }}
              validationMessages={validationMessages?.transferSource}
              showPopover={false}
            />
          ) : null}
        </Col>
        <Col md={3}>
          <DateInput
            {...getPaymentDateMinMax(data)}
            variant="horizontal"
            className={styles.inputDivider}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.paymentDate}
            value={data.paymentDate}
            onChangeDate={(value) =>
              setDataHandleDatesClearValidation({ paymentDate: value })
            }
            validationMessages={validationMessages?.paymentDate}
            showPopover={false}
          />
          <DateInput
            {...getPromiseDateMinMax(data)}
            variant="horizontal"
            className={styles.inputDivider}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.promiseDate}
            value={data.promiseDate}
            onChangeDate={(value) =>
              setDataHandleDatesClearValidation({ promiseDate: value })
            }
            validationMessages={validationMessages?.promiseDate}
            showPopover={false}
          />
          <DateInput
            {...getWorkDateMinMax(data)}
            variant="horizontal"
            className={styles.inputDivider}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.workDate}
            value={data.workDate}
            onChangeDate={(value) =>
              setDataHandleDatesClearValidation({ workDate: value })
            }
            validationMessages={validationMessages?.workDate}
            showPopover={false}
          />
          {paymentMethodRequiresCallBack(data.paymentMethod) && (
            <DateInput
              {...getCallBackDateMinMax(data)}
              variant="horizontal"
              className={styles.inputDivider}
              innerContentClassName={styles.inputContent}
              labelClassName={styles.inputLabel}
              inputClassName={styles.inputValue}
              label={strings.callBackDate}
              value={data.callBackDate}
              onChangeDate={(value) =>
                setDataHandleDatesClearValidation({ callBackDate: value })
              }
              validationMessages={validationMessages?.callBackDate}
              showPopover={false}
            />
          )}
        </Col>
        <Col md={3}>
          {(channel === "INB" || channel === "TRN") && (
            <StyledInput
              variant="horizontal"
              className={styles.inputDivider}
              innerContentClassName={styles.inputContent}
              labelClassName={styles.inputLabel}
              inputClassName={styles.inputValue}
              label={strings.confirmNum}
              value={data.confirmationNumber}
              onChange={(e) =>
                setDataHandleDatesClearValidation({
                  confirmationNumber: e.target.value,
                })
              }
              validationMessages={validationMessages?.confirmationNumber}
              showPopover={false}
            />
          )}
          <StyledInput
            disabled={true}
            variant="horizontal"
            className={styles.inputDivider}
            innerContentClassName={styles.inputContent}
            labelClassName={styles.inputLabel}
            inputClassName={styles.inputValue}
            label={strings.actionEntry}
            value={getResultActionEntry({
              tab: "PAYMENT",
              data,
            })}
            validationMessages={validationMessages?.actionEntry}
            showPopover={false}
          />
          <PermCollQueue
            dwbuid={dwbuid}
            onDisplayGeneralErrors={onDisplayGeneralErrors}
            channel={channel}
            referenceId={referenceId}
          />
        </Col>
        <Col md={3}>
          <Note
            tab="PAYMENT"
            data={{
              ...data,
              contactPerson,
            }}
          />
        </Col>
      </Row>
      <ConfirmationModal
        isOpen={isDateOverrideOpenWith !== null}
        title={
          isDateOverrideOpenWith?.paymentMethod
            ? strings.a0122Title
            : strings.a0123Title
        }
        message={
          isDateOverrideOpenWith?.paymentMethod
            ? strings.a0122Body
            : strings.a0123Body(!!isDateOverrideOpenWith?.callBackDate)
        }
        cancelText={strings.overrideCancelText}
        confirmText={strings.overrideConfirmText}
        onRequestClose={() => {
          if (isDateOverrideOpenWith === null) {
            // NOTE: This should not be possible
            return;
          }
          setDataHandleDatesClearValidation(isDateOverrideOpenWith, false);
          setIsDateOverrideOpenWith(null);
        }}
        onConfirm={() => {
          if (isDateOverrideOpenWith === null) {
            // NOTE: This should not be possible
            return;
          }
          setDataHandleDatesClearValidation(isDateOverrideOpenWith, true);
          setIsDateOverrideOpenWith(null);
        }}
      />
    </>
  );
}
